import { DateTime } from 'luxon';
import type { JSX } from 'react';
import { useStore } from 'dg-web-shared/lib/Flux.tsx';
import { useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import { SingleSelection } from '../../../ui/labeled-elements/SingleSelection.tsx';
import * as FilteredPermitsState from '../../state/FilteredPermitsState.ts';
import * as Text from '../../../common/i18n/Text.ts';
import { validDateFilterTexts } from '../../i18n/FilteredPermitsTexts.ts';
import { DatePicker } from '../../../ui/date-picker/DatePicker.tsx';
import {
    HalfSlideIn,
    SecondLevelHeader,
    SlideInBody,
    SlideInHeaderTexts,
} from '../../../ui/slidein/Slidein.tsx';
import * as FilteredPermitsActions from '../../actions/FilteredPermitsActions.ts';
import { DateHeader } from '../shared/DateHeader.tsx';
import { css } from '@emotion/css';

export interface ValidDateFilterTexts {
    filterTitle: Text.Translation;
    from: Text.Translation;
    to: Text.Translation;
}

export function ValidDateFilterHalfSlideIn() {
    const { storeState, update } = useStore(store => ({
        filter: FilteredPermitsState.Filter.get(store),
    }));
    const { language } = useLanguage();

    function getTexts(): ValidDateFilterTexts {
        return validDateFilterTexts[language];
    }

    function renderFromDatePicker(): JSX.Element {
        const texts = getTexts();
        return (
            <div className={css({ marginBottom: '30px' })}>
                <DateHeader
                    label={texts.from()}
                    date={storeState.filter.validDateFrom}
                />
                <DatePicker
                    currentDate={DateTime.now()}
                    selectedDate={storeState.filter.validDateFrom}
                    onSelect={d =>
                        update(store =>
                            FilteredPermitsActions.setValidDateFrom(store, d),
                        )
                    }
                    language={language}
                />
            </div>
        );
    }

    function renderToDatePicker(): JSX.Element {
        const texts = getTexts();
        return (
            <div>
                <DateHeader
                    label={texts.to()}
                    date={storeState.filter.validDateTo}
                />
                <DatePicker
                    currentDate={DateTime.now()}
                    selectedDate={storeState.filter.validDateTo}
                    onSelect={d =>
                        update(store =>
                            FilteredPermitsActions.setValidDateTo(store, d),
                        )
                    }
                    language={language}
                />
            </div>
        );
    }

    const texts = validDateFilterTexts[language];
    return (
        <HalfSlideIn open={storeState.filter.validDateSelectionVisible}>
            <SecondLevelHeader
                onClose={() =>
                    update(store =>
                        FilteredPermitsState.Filter.stateWrite(store, {
                            validDateSelectionVisible: false,
                        }),
                    )
                }
            >
                <SlideInHeaderTexts
                    subtitle={''}
                    title={texts.filterTitle()}
                    hasLeftIcon={false}
                />
            </SecondLevelHeader>
            <SlideInBody>
                <div
                    className={css({
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        left: 0,
                        right: 0,
                        overflowX: 'hidden',
                        overflowY: 'auto',
                    })}
                >
                    <div
                        className={css({
                            width: '280px',
                            margin: '30px auto 0 auto',
                        })}
                    >
                        {renderFromDatePicker()}
                        {renderToDatePicker()}
                    </div>
                </div>
            </SlideInBody>
        </HalfSlideIn>
    );
}

export function ValidDateFilterSelection() {
    const { storeState, update } = useStore(store => ({
        filter: FilteredPermitsState.Filter.get(store),
    }));
    const { language } = useLanguage();

    const texts = validDateFilterTexts[language];
    const selection = Formatter.openRange(
        storeState.filter.validDateFrom,
        storeState.filter.validDateTo,
        Formatter.dayMonthYear,
    );

    const write = (s: Partial<FilteredPermitsState.Filter.State>) =>
        update(store => FilteredPermitsState.Filter.stateWrite(store, s));

    const clear = FilteredPermitsState.Filter.validDateFilterActive(
        storeState.filter,
    )
        ? () => update(FilteredPermitsActions.clearValidDate)
        : null;

    return (
        <div>
            <SingleSelection
                label={texts.filterTitle()}
                focused={false}
                selection={selection}
                onClick={() => write({ validDateSelectionVisible: true })}
                onClear={clear}
            />
        </div>
    );
}
